import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

function Aml() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });    
      }
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Cardplot</title>
        <meta
          name="description"
          content="Learn how Cardplot collects, uses, stores, and protects your personal data while providing secure cross-border payments and digital financial services across Africa."
        />
        <meta
          name="keywords"
          content="Cardplot privacy policy, data protection, personal data, NDPR, GDPR, data security, user privacy, fintech Africa"
        />
        <meta property="og:title" content="Privacy Policy | Cardplot" />
        <meta
          property="og:description"
          content="Read Cardplot's privacy policy to understand how your personal information is handled and protected."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cardplot.com/privacy-policy" />
        <meta property="og:image" content="https://cardplot.com/favicon.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | Cardplot" />
        <meta
          name="twitter:description"
          content="Cardplot is committed to safeguarding your privacy and securing your personal information across all services."
        />
      </Helmet>
      <section className="breadcrumb__section bg-overlay">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="breadcrumb__text">
                <h3 className="breadcrumb__text__head">Privacy Policy</h3>
                <p className="breadcrumb__text__content color__blue size_pricing">
                  At Cardplot, your privacy and security come first. We are a Pan-African fintech
                  platform that enables secure cross-border payments across Africa. To deliver safe and
                  compliant financial services, we collect and process certain personal information
                  responsibly and transparently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="legal__section">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="legal__section__content">
                <h4 className="legal__section__content__head">1. What We Collect</h4>
                <p className="legal__section__content__text">
                  We collect only the information necessary to provide our services, including:
                </p>
                <ul className="legal__section__content__text">
                  <li>Full name, phone number, and email</li>
                  <li>Date of birth and residential address</li>
                  <li>Government-issued ID and selfie verification</li>
                  <li>Bank account details and (where required) BVN</li>
                  <li>Business registration documents (for business accounts)</li>
                  <li>Transaction history and wallet activity</li>
                  <li>Device and technical usage data</li>
                </ul>
                <p className="legal__section__content__text">
                  We also conduct identity verification, AML, sanctions, and PEP screening as required
                  by law.
                </p>

                <h4 className="legal__section__content__head">2. Why We Collect Your Data</h4>
                <p className="legal__section__content__text">We use your information to:</p>
                <ul className="legal__section__content__text">
                  <li>Create and manage your account</li>
                  <li>Verify your identity (KYC/KYB)</li>
                  <li>Process payments and cross-border transactions</li>
                  <li>Create virtual accounts and bank accounts</li>
                  <li>Prevent fraud and financial crime</li>
                  <li>Comply with regulatory requirements</li>
                  <li>Improve our services</li>
                </ul>
                <p className="legal__section__content__text">
                  We do not sell your personal data.
                </p>

                <h4 className="legal__section__content__head">3. How We Protect Your Information</h4>
                <p className="legal__section__content__text">
                  We use industry-standard security measures, including:
                </p>
                <ul className="legal__section__content__text">
                  <li>End-to-end encryption</li>
                  <li>Secure cloud infrastructure</li>
                  <li>Multi-factor authentication (MFA)</li>
                  <li>Role-based internal access controls</li>
                  <li>Continuous fraud monitoring</li>
                </ul>
                <p className="legal__section__content__text">
                  Access to your data is strictly limited to authorized personnel.
                </p>

                <h4 className="legal__section__content__head">4. Data Sharing</h4>
                <p className="legal__section__content__text">
                  We only share data with regulated and trusted partners necessary to provide our
                  services, including:
                </p>
                <ul className="legal__section__content__text">
                  <li>Identity verification providers</li>
                  <li>Compliance screening partners</li>
                  <li>Banking and payment infrastructure providers</li>
                </ul>
                <p className="legal__section__content__text">
                  All partners are required to protect your data and use it only for authorized
                  purposes.
                </p>

                <h4 className="legal__section__content__head">5. How Long We Keep Your Data</h4>
                <p className="legal__section__content__text">
                  We retain your information only as long as required to:
                </p>
                <ul className="legal__section__content__text">
                  <li>Provide our services</li>
                  <li>Meet legal and regulatory obligations</li>
                  <li>Prevent fraud and resolve disputes</li>
                </ul>
                <p className="legal__section__content__text">
                  Afterward, data is securely deleted or anonymized.
                </p>

                <h4 className="legal__section__content__head">6. Your Rights</h4>
                <p className="legal__section__content__text">You may have the right to:</p>
                <ul className="legal__section__content__text">
                  <li>Access your personal data</li>
                  <li>Request corrections</li>
                  <li>Request deletion (subject to legal requirements)</li>
                  <li>Withdraw consent where applicable</li>
                </ul>
                <p className="legal__section__content__text">
                  To make a request, contact us through our official support channels.
                </p>

                <h4 className="legal__section__content__head">7. Updates to This Policy</h4>
                <p className="legal__section__content__text">
                  We may update this policy periodically. Any significant changes will be communicated
                  through our website or app.
                </p>
                <p className="legal__section__content__text">
                  Last Updated Date: January 10th, 2026
                </p>

                <h4 className="legal__section__content__head">Contact Us</h4>
                <p className="legal__section__content__text">
                  For privacy-related questions:
                  <br />
                  Cardplot Technologies Limited
                  <br />
                  Email:{' '}
                  <a href="/">
                    legal@cardplot.com
                  </a>
                  <br />
                  Website:{' '}
                  <a href="https://cardplot.com" target="_blank" rel="noopener noreferrer">
                    https://cardplot.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}


export default Aml